import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { message, Spin } from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { OrderDetail, OrderStatus } from './types';
import { approveOrderApi, rejectOrderApi } from './api';
import { HeaderOrderDetail } from './components/HeaderOrderDetail';
import { FlightInfoSection } from './components/FlightInfoSection';
import { TicketInfoSection } from './components/TicketInfoSection';
import { PriceInfoSection } from './components/PriceInfoSection';
import { PassengerInfoTable } from './components/PassengerInfoTable';
import { ContactInfoSection } from './components/ContactInfoSection';
import { calculateCountdown } from './helpers';
import {
  getInfoOrderHolidayApi,
  getFlightDetailApi,
} from 'app/services/holidayApi';

interface RouteParams {
  orderCode: string;
}

export const OrderDetailPage: React.FC = () => {
  const history = useHistory();
  const { orderCode } = useParams<RouteParams>();
  const translations = useSelector(selectTranslations);
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [countdown, setCountdown] = useState<string>('');

  useEffect(() => {
    fetchOrderDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderCode]);

  // Countdown timer
  useEffect(() => {
    if (!order) return;

    const timer = setInterval(() => {
      const timeLeft = calculateCountdown(order.expiredAt);
      setCountdown(timeLeft);
    }, 1000);

    return () => clearInterval(timer);
  }, [order]);

  const fetchOrderDetail = useCallback(async () => {
    setLoading(true);
    try {
      // Call order detail API
      const res = await getInfoOrderHolidayApi({ orderCode });

      if (!res || !res.data) {
        message.error(
          translations?.['b2b.txt.orderNotFound'] || 'Không tìm thấy đơn hàng',
        );
        history.push('/approve-order?tab=holiday');
        return;
      }

      const item = res.data;

      // Map backend status value to OrderStatus enum
      const statusMap: Record<string, OrderStatus> = {
        pendingConfirmation: OrderStatus.PENDING_CONFIRMATION,
        confirmed: OrderStatus.CONFIRMED,
        unconfirmed: OrderStatus.UNCONFIRMED,
        cancelled: OrderStatus.CANCELLED,
      };

      // Helper to convert "HH:mm:ss DD/MM/YYYY" to ISO format
      const convertToIso = (dateStr: string): string => {
        if (!dateStr) return '';
        const parsed = moment(dateStr, 'HH:mm:ss DD/MM/YYYY', true);
        return parsed.isValid() ? parsed.toISOString() : dateStr;
      };

      // Get data from orderItems
      const flights = item.orderItems?.flights || [];
      const tickets = item.orderItems?.tickets || [];
      const combo = item.orderItems?.combo?.[0] || {}; // Get first combo

      // Calculate total passenger count (adult + child, not infant)
      const totalPaxCount = tickets.reduce((sum: number, ticket: any) => {
        return sum + (ticket.adultQuantity || 0) + (ticket.childQuantity || 0);
      }, 0);

      // Map tickets to combo format
      const mappedTickets = tickets.map((t: any) => ({
        name: t.name,
        quantity: t.quantity,
      }));

      // Fetch passenger details if bookingId exists
      let passengerData = undefined;
      let flightContactInfo: any = null;
      if (flights.length > 0 && flights[0].bookingId) {
        try {
          const passengerRes = await getFlightDetailApi({
            bookingId: flights[0].bookingId,
          });
          if (passengerRes?.data?.listPassenger) {
            passengerData = passengerRes.data.listPassenger.map((p: any) => ({
              type: p.type || 'ADULT',
              title: p.title || '',
              firstName: p.firstName || '',
              lastName: p.lastName || '',
              dateOfBirth: p.dateOfBirth || '',
              gender: '',
              documentType: '',
              documentNumber: '',
            }));
          }
          // Get contact info from flight detail API
          if (passengerRes?.data?.contactInfo) {
            flightContactInfo = passengerRes.data.contactInfo;
          }
        } catch (error) {
          console.warn('Failed to fetch passenger details:', error);
        }
      }

      // Map to OrderDetail format
      const orderDetail: OrderDetail = {
        id: item.idHash || String(item.id),
        orderCode: item.holidaySubmitNumber,
        pnr: flights[0]?.pnr || item.holidaySubmitNumber,
        createdAt: convertToIso(item.orderDate),
        paymentMethod: item.payment?.name || item.payment?.code || '',
        flightType:
          flights.length > 1 ? ('ROUND_TRIP' as any) : ('ONE_WAY' as any),
        orderItems: [
          {
            combo: {
              quantity: totalPaxCount || item.totalTicket || 0,
              name: combo.comboName || item.items?.productName || '',
              comboName: combo.comboName || item.items?.productName || '',
              salePrice: parseFloat(item.totalPayment || item.totalPrice) || 0,
            },
            flight: {
              departure: flights[0]?.departure || '',
              arrival:
                flights[flights.length - 1]?.arrival ||
                flights[0]?.arrival ||
                '',
              departureTime: flights[0]?.departureTime || '',
              arrivalTime:
                flights[flights.length - 1]?.arrivalTime ||
                flights[0]?.arrivalTime ||
                '',
              flightNumber: flights[0]?.flightNumber || '',
              carrier: flights[0]?.carrier || '',
              cabinClass: flights[0]?.bookingClass || 'Economy',
            },
            tickets: mappedTickets,
          },
        ],
        totalPayment: parseFloat(item.totalPayment || item.totalPrice) || 0,
        expiredAt: item.expiredAt || item.expireAt,
        agent: item.agent?.name || '',
        subOrderState:
          statusMap[item.subOrderState?.value] || OrderStatus.CANCELLED,
        passengers: passengerData,
        contactInfo: {
          fullName: flightContactInfo?.fullName || item.customerFullName || '',
          email: flightContactInfo?.email || item.customerEmail || '',
          phoneCode: '+84',
          phoneNumber:
            flightContactInfo?.phoneNumber
              ?.replace(/^\+84/, '')
              .replace(/-.*$/, '') ||
            item.customerPhone?.replace(/^\+84/, '') ||
            '',
        },
        thirdPartySubmitNumber: item.thirdPartySubmitNumber || '',
      };

      setOrder(orderDetail);
    } catch (error) {
      message.error(
        translations?.['b2b.txt.errorLoadOrder'] ||
          'Không thể tải thông tin đơn hàng',
      );
      history.push('/approve-order?tab=holiday');
    } finally {
      setLoading(false);
    }
  }, [orderCode, history]);

  const handleApprove = async () => {
    if (!order) return;

    setActionLoading(true);
    try {
      await approveOrderApi(order.id);
      message.success({
        content:
          translations?.['b2b.txt.approveOrderSuccess'] ||
          'Quý khách đã phê duyệt đơn hàng thành công',
        duration: 2,
        onClose: () => {
          history.push('/approve-order?tab=holiday');
        },
      });
    } catch (error: any) {
      if (error.errorCode === 'MSG022') {
        message.warning(
          translations?.['b2b.txt.orderCanceledBySale'] ||
            'MSG022: Đơn hàng đã bị hủy bởi sale',
        );
      } else if (error.errorCode === 'MSG023') {
        message.warning(
          translations?.['b2b.txt.orderHoldExpired'] ||
            'MSG023: Đơn hàng đã hết thời gian giữ',
        );
      } else {
        message.error(
          error.message ||
            translations?.['b2b.txt.approveError'] ||
            'Có lỗi xảy ra khi phê duyệt đơn hàng',
        );
      }
      setTimeout(() => history.push('/approve-order?tab=holiday'), 2000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!order) return;

    setActionLoading(true);
    try {
      await rejectOrderApi(order.id);
      message.success({
        content:
          translations?.['b2b.txt.rejectOrderSuccess'] ||
          'Quý khách đã từ chối đơn hàng thành công',
        duration: 2,
        onClose: () => {
          history.push('/approve-order?tab=holiday');
        },
      });
    } catch (error: any) {
      if (error.errorCode === 'MSG022') {
        message.warning(
          translations?.['b2b.txt.orderCanceledBySale'] ||
            'MSG022: Đơn hàng đã bị hủy bởi sale',
        );
      } else if (error.errorCode === 'MSG023') {
        message.warning(
          translations?.['b2b.txt.orderHoldExpired'] ||
            'MSG023: Đơn hàng đã hết thời gian giữ',
        );
      } else {
        message.error(
          error.message ||
            translations?.['b2b.txt.rejectError'] ||
            'Có lỗi xảy ra khi từ chối đơn hàng',
        );
      }
      setTimeout(() => history.push('/approve-order?tab=holiday'), 2000);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <LoadingWrapper>
        <Spin size="large" />
      </LoadingWrapper>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <PageWrapper>
      <HeaderOrderDetail
        order={order}
        countdown={countdown}
        actionLoading={actionLoading}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      <ContentWrapper>
        <BoxPassengerInformation>
          <Title>
            {translations?.['b2b.txt.journeyInfo'] || 'Thông tin hành trình'}
          </Title>
          <Line />
          <FlightInfoSection order={order} />
        </BoxPassengerInformation>

        <BoxPassengerInformation>
          <Title>
            {translations?.['b2b.txt.parkTicketInfo'] ||
              'Thông tin vé công viên'}
          </Title>
          <Line />
          <TicketInfoSection orderItems={order.orderItems} />
        </BoxPassengerInformation>

        <BoxPassengerInformation>
          <Title>
            {translations?.['b2b.txt.fareInfo'] || 'Thông tin giá vé'}
          </Title>
          <Line />
          <PriceInfoSection order={order} />
        </BoxPassengerInformation>

        {/* Passenger info not available from listing API yet */}
        {order.passengers && order.passengers.length > 0 && (
          <BoxPassengerInformation>
            <Title>
              {translations?.['b2b.txt.passengerInfoNoticeTitle'] ||
                'Thông tin hành khách'}
            </Title>
            <Line />
            <PassengerInfoTable passengers={order.passengers} />
          </BoxPassengerInformation>
        )}

        {/* Contact info not available from listing API yet */}
        {order.contactInfo && (
          <BoxPassengerInformation>
            <Title>
              {translations?.['b2b.txt.contactFormTitle'] ||
                'Thông tin liên hệ'}
            </Title>
            <Line />
            <ContactInfoSection contactInfo={order.contactInfo} />
          </BoxPassengerInformation>
        )}
      </ContentWrapper>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 24px auto;
  padding: 0 24px;
`;

const BoxPassengerInformation = styled.div`
  background-color: #fff;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const Line = styled.div`
  height: 1px;
  background-color: #e5e5e5;
  margin: 12px -11px;
`;
